import {
    faBars,
    faCaretDown,
    faCaretRight,
    faCircleExclamation,
    faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, merge } from 'lodash-es';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BehaviorSubject } from 'rxjs';
import { useObservableState } from 'observable-hooks';
import { Link } from 'react-router-dom';
import { Profile, ProfileProvider, useProfile } from '../../profile';
import schema from '../../schema.json';
import BetaBadge from '../betaBadge/BetaBadge';
import Button from '../form/Button';
import NotificationBadge from '../notificationBadge/NotificationBadge';
import logo from './omnidots-logo.svg';
import {
    accountMenuId,
    downloadsMenuId,
    languageMap,
    languagePlaceholder,
    websiteUrl,
} from './utils';

const listIconClasses = 'xl:ml-1.5 h-3 w-3 fill-current';
const menuItemNameClasses =
    'inline-flex h-20 px-6 xl:px-8 items-center whitespace-nowrap cursor-pointer text-menu-color no-underline justify-between';
const menuItemListClasses = 'inline-flex flex-col group hover:bg-menu-hover xl:border-0';

function flipState(isOpen) {
    return isOpen
        ? // Open for mobile, but hidden for desktop.
          'xl:hidden'
        : // Hidden for mobile, and (somehow?) hidden for desktop.
          'hide';
}

function useTranslatedMenu() {
    const { t } = useTranslation();

    return useMemo(() => {
        const translations = t('MENU', { returnObjects: true });
        return merge([], schema.menu, translations);
    }, [t]);
}

function shouldBeVisible({ checks = [] }, profile) {
    const failedCheck = checks.find((check) => !profile[check]);

    if (failedCheck) {
        return false;
    }

    return true;
}

function filterMenuForProfile(items, profile, forceOpenMenuId) {
    const { isProfileIncomplete, readyReportsCount } = profile;
    function showIncompleteIcon(id) {
        return (isProfileIncomplete || readyReportsCount > 0) && id === accountMenuId;
    }
    function showNotificationBadge(id) {
        return readyReportsCount > 0 && id === downloadsMenuId;
    }

    const filteredItems = items
        .filter((item) => shouldBeVisible(item, profile))
        .map((item) => {
            const [children, childForcedOpen] = filterMenuForProfile(
                item.children,
                profile,
                forceOpenMenuId
            );
            const isForcedOpen =
                childForcedOpen || (forceOpenMenuId ? item.id === forceOpenMenuId : false);

            return {
                ...item,
                showIncompleteIcon: showIncompleteIcon(item.id),
                notificationCount: (showNotificationBadge(item.id) && readyReportsCount) || 0,
                // Replaces any URL containing the languagePlaceholder string with
                // the active language code.
                url: item.url && item.url.replace(languagePlaceholder, profile.languageCode),
                children,
                isForcedOpen,
            };
        });

    // Check if any of the items are forced open. If so, this information is passed
    // back to the parent menu item so it can also be marked as forced open.
    const isForcedOpen = filteredItems.some((item) => item.isForcedOpen);

    return [filteredItems, isForcedOpen];
}

function useMenuForProfile(profile, forceOpenMenuId) {
    const menu = useTranslatedMenu();

    return useMemo(() => {
        const [items] = filterMenuForProfile(menu, profile, forceOpenMenuId);
        return items;
    }, [menu, profile, forceOpenMenuId]);
}

const menuItemShape = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    url: PropTypes.string,
    beta: PropTypes.bool,
    notificationCount: PropTypes.number.isRequired,
    showHelpIcon: PropTypes.bool,
    showIncompleteIcon: PropTypes.bool.isRequired,
    isForcedOpen: PropTypes.bool.isRequired,
    children(...args) {
        return PropTypes.arrayOf(PropTypes.shape(this)).apply(this, args);
    },
};

const MenuContext = createContext();

function ButtonElement({ children, url, ...props }) {
    const { spaMode } = useContext(MenuContext);

    if (!url) {
        // If no URL is provided, render a simple button element.
        return <button {...props}>{children}</button>;
    }

    // In SPA (Single Page Application) mode, typically for environments like Storybook tests,
    // navigation occurs via the React Router `Link` component.
    // This enables client-side routing: the BrowserRouter handles navigation internally,
    // updating the view without requiring a full page reload from the browser.
    if (spaMode) {
        return (
            <Link to={url} {...props}>
                {children}
            </Link>
        );
    }

    // For traditional navigation (e.g., when this menu is used within the Django application),
    // a standard <a> tag is used. This causes the browser to make a full request
    // to the server for the new page.
    return (
        <a href={url} {...props}>
            {children}
        </a>
    );
}
ButtonElement.propTypes = {
    url: PropTypes.string,
    children: PropTypes.node.isRequired,
};

function SubMenuContainer({ isOpen, isForcedOpen = false, children }) {
    return (
        <div className="flex-none">
            <ul
                className={`divide-y xl:absolute xl:w-64 xl:border xl:group-hover:flex xl:group-hover:flex-col ${
                    isForcedOpen ? 'xl:flex xl:flex-col' : flipState(isOpen)
                }`}
            >
                {children}
            </ul>
        </div>
    );
}
SubMenuContainer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    isForcedOpen: PropTypes.bool,
    children: PropTypes.node.isRequired,
};

function SubMenuItem({
    id,
    name,
    children,
    url,
    beta,
    notificationCount,
    level = 1,
    swarmIcon,
    isForcedOpen,
}) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <li
            className={`${level === 1 && 'group/sub'} flex flex-col bg-white hover:bg-menu-hover`}
            onClick={() => setIsOpen((openState) => !openState)}
        >
            <ButtonElement
                className={`${level === 1 ? 'pl-12' : 'pl-20'} ${menuItemNameClasses} xl:order-2`}
                url={url}
                id={id}
            >
                <span className="flex items-center">
                    {!!swarmIcon && <div className={`${swarmIcon} mr-1.5 inline-block h-6 w-8`} />}
                    {name}
                    {beta && <BetaBadge className="ml-1" />}
                    {!!notificationCount && <NotificationBadge number={notificationCount} />}
                </span>
                {!isEmpty(children) && (
                    <>
                        <FontAwesomeIcon
                            icon={faCaretRight}
                            className={`${listIconClasses} hide xl:block`}
                        />
                        <FontAwesomeIcon
                            icon={faCaretDown}
                            className={`${listIconClasses} block xl:hidden`}
                        />
                    </>
                )}
            </ButtonElement>
            {!isEmpty(children) && (
                <div className="order-2 xl:relative xl:order-1">
                    <ul
                        className={`${
                            isForcedOpen ? 'xl:block' : flipState(isOpen)
                        } divide-y border xl:absolute xl:left-full xl:-mt-px xl:w-full ${
                            level === 1 && 'xl:group-hover/sub:block'
                        }`}
                    >
                        {children.map((item, i) => (
                            <SubMenuItem {...item} key={i} level={level + 1} />
                        ))}
                    </ul>
                </div>
            )}
        </li>
    );
}
SubMenuItem.propTypes = {
    ...menuItemShape,
    level: PropTypes.number,
};
function LanguageMenuItem({ item }) {
    const { csrfToken } = useProfile();
    return (
        <form action="/i18n/" method="post" className={item}>
            <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken}></input>
            <input name="next" type="hidden" value={window.location.pathname}></input>
            <input name="language" type="hidden" value={item}></input>
            <input
                type="submit"
                value={languageMap[item]}
                className={
                    'h-20 w-full cursor-pointer pl-12 pr-8 text-left text-menu-color xl:w-64 xl:px-8'
                }
            ></input>
        </form>
    );
}
LanguageMenuItem.propTypes = {
    item: PropTypes.string.isRequired,
};

function LanguageMenuList({ code }) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <button
                className={menuItemNameClasses}
                onClick={() => setIsOpen((openState) => !openState)}
            >
                {languageMap[code]}
                <FontAwesomeIcon icon={faCaretDown} className={listIconClasses} />
            </button>
            <SubMenuContainer isOpen={isOpen}>
                {Object.keys(languageMap).map((item, i) => (
                    <li key={i} className="bg-white hover:bg-menu-hover">
                        <LanguageMenuItem item={item} />
                    </li>
                ))}
            </SubMenuContainer>
        </>
    );
}
LanguageMenuList.propTypes = {
    code: PropTypes.string,
    language: PropTypes.string,
};

function MenuItem({ id, name, children, url, showHelpIcon, showIncompleteIcon, isForcedOpen }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <ButtonElement
                className={menuItemNameClasses}
                url={url}
                id={id}
                onClick={() => setIsOpen((openState) => !openState)}
            >
                {!showHelpIcon ? (
                    <div>
                        {showIncompleteIcon && (
                            <FontAwesomeIcon
                                icon={faCircleExclamation}
                                className="h-3 w-3 pb-0.5 pr-1 text-warning xl:pb-0"
                                title={gettext('INCOMPLETE_PROFILE')}
                            />
                        )}
                        {name}
                    </div>
                ) : (
                    <FontAwesomeIcon icon={faQuestionCircle} className="h-4 w-4 fill-current" />
                )}
                {!isEmpty(children) && (
                    <FontAwesomeIcon icon={faCaretDown} className={listIconClasses} />
                )}
            </ButtonElement>
            <SubMenuContainer isOpen={isOpen} isForcedOpen={isForcedOpen}>
                {children.map((item, i) => (
                    <SubMenuItem {...item} key={i} />
                ))}
            </SubMenuContainer>
        </>
    );
}
MenuItem.propTypes = menuItemShape;

export function Menu({ profile, forceMenuOpenById$, spaMode = false }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const { logoutUrlPath, loginUrlPath } = schema;
    const {
        isAuthenticated,
        languageCode,
        resellerData: { isActive, topLogoUrl, companyName },
    } = profile;
    const forceOpenMenuId = useObservableState(forceMenuOpenById$);
    const menu = useMenuForProfile(profile, forceOpenMenuId);

    const redirectPath =
        window.location.pathname === logoutUrlPath || loginUrlPath ? '/' : window.location.pathname;

    const handleAuthButton = useCallback(
        () =>
            window.location.replace(
                `${isAuthenticated ? logoutUrlPath : loginUrlPath}?next=${redirectPath}`
            ),
        [isAuthenticated, loginUrlPath, logoutUrlPath, redirectPath]
    );

    return (
        <MenuContext.Provider value={{ spaMode }}>
            <ProfileProvider profile={profile}>
                <div className="bg-white shadow-menu">
                    <nav className="relative z-[98] m-auto flex h-20 max-w-screen-2xl flex-wrap items-stretch justify-between border-gray-outline px-4">
                        <div className="flex flex-[1_1_10%] items-center">
                            <a className="p-1" href={websiteUrl}>
                                <img
                                    src={isActive && topLogoUrl ? topLogoUrl : logo}
                                    alt={companyName || 'Omnidots'}
                                    className="w-52"
                                />
                            </a>
                        </div>
                        <div className="inline-flex items-center xl:hidden">
                            <button
                                className="inline-flex items-center p-2 hover:bg-menu-hover"
                                onClick={() => setIsOpen(!isOpen)}
                            >
                                <FontAwesomeIcon icon={faBars} className="h-8 w-8" />
                            </button>
                        </div>
                        <div
                            className={`${
                                isOpen ? '' : 'hide'
                            } absolute inset-x-0 top-20 block divide-y border-b bg-white text-lg xl:relative xl:top-0 xl:flex xl:items-stretch xl:divide-y-0 xl:border-0 xl:text-xs`}
                        >
                            <ul
                                className={
                                    'box-content flex flex-col items-stretch divide-y xl:flex-grow xl:flex-row xl:divide-y-0'
                                }
                            >
                                {menu.map((item, i) => (
                                    <li className={`${menuItemListClasses}`} key={i}>
                                        <MenuItem {...item} />
                                    </li>
                                ))}
                                <li className={`${menuItemListClasses} language-menu`}>
                                    <LanguageMenuList code={languageCode} />
                                </li>
                            </ul>
                            <div className="flex items-center py-4 pl-4">
                                <Button
                                    variant={isAuthenticated ? 'secondary' : 'primary'}
                                    className="whitespace-nowrap font-bold hover:bg-[#A6A6A6]"
                                    onClick={handleAuthButton}
                                    id="logout"
                                >
                                    {isAuthenticated ? t('Sign out') : t('Log in')}
                                </Button>
                            </div>
                        </div>
                    </nav>
                </div>
            </ProfileProvider>
        </MenuContext.Provider>
    );
}
Menu.propTypes = {
    profile: PropTypes.instanceOf(Profile).isRequired,
    forceMenuOpenById$: PropTypes.instanceOf(BehaviorSubject).isRequired,
    spaMode: PropTypes.bool,
};
